/**
 * Authentication utilities for JWT token management
 */

/**
 * Verifies if a JWT token has expired
 * @param {string} token - JWT token to verify
 * @returns {boolean} - True if token is expired or invalid
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const decoded = JSON.parse(atob(token.split('.')[1])); // Decode JWT payload
    return decoded.exp < Date.now() / 1000; // Check if expired
  } catch (error) {
    console.error('Error decoding token:', error);
    return true; // Consider expired if decoding fails
  }
};

/**
 * Checks if user is authenticated with a valid token
 * @returns {boolean} - True if user has valid, non-expired token
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  return !!token && !isTokenExpired(token);
};

/**
 * Validates token with the server
 * @returns {Promise<boolean>} - True if token is valid on server
 */
export const checkTokenValidity = async () => {
  const token = localStorage.getItem('token');
  
  if (!token || isTokenExpired(token)) {
    return false;
  }
  
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-token`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.ok;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
};

/**
 * Removes token from localStorage
 */
export const clearAuthToken = () => {
  localStorage.removeItem('token');
};

/**
 * Stores token in localStorage
 * @param {string} token - JWT token to store
 */
export const setAuthToken = (token) => {
  localStorage.setItem('token', token);
};
